import { inject, Injectable } from '@angular/core';

import { calendarWallTimeToUtc } from '@blaze-runtime/engine-util';
import { FormContext, getActiveBinding } from '@blaze-shared/elements-domain';
import { SchemaService } from '@blaze-shared/schema-data-access';
import {
  DatasetColumn,
  findColumn,
  isDatasetLinkValue,
  isDatasetLinkValueLike,
  isLinkedInlineColumn,
} from '@blaze-shared/schema-domain';
import { valueToBool } from '@blaze-shared/shared-util';

import { isTimezoneDropdown } from '../../../utils/elements/element.utils';
import { hasDataProps } from '../../element-model/element-props-guards';
import { GetChildValueParams } from '../models/children-handler.models';

export interface ChildValueResult {
  value: any;
  converted: boolean;
  extracted: boolean;
  triggerFormulasRefresh?: boolean;
}

type ValueResult = Omit<ChildValueResult, 'triggerFormulasRefresh'>;

@Injectable({ providedIn: 'root' })
export class ChildValueExtractor {
  private readonly schemaService = inject(SchemaService);

  /**
   * Extracts and coerces a child element's event data into the value expected
   * by the parent form container field.
   */
  extract(params: GetChildValueParams): ChildValueResult {
    const { formDataBindings } = params;
    const unwrapped = this.unwrapEventData(params);

    if (!formDataBindings?.length) {
      return { ...unwrapped, triggerFormulasRefresh: false };
    }

    const triggerFormulasRefresh = this.shouldTriggerFormulasRefresh(params);
    const coerced = this.coerceByColumnType(params, unwrapped);

    return { ...coerced, triggerFormulasRefresh };
  }

  private unwrapEventData({
    eventData,
    dataValueField,
    preventLinksExtraction,
  }: GetChildValueParams): ValueResult {
    const canUnwrap =
      !!eventData && typeof dataValueField === 'string' && typeof eventData === 'object';
    if (!canUnwrap) {
      return { value: eventData, converted: false, extracted: false };
    }

    if (Array.isArray(eventData)) {
      return this.unwrapArray(eventData, dataValueField as string, preventLinksExtraction);
    }

    if (isDatasetLinkValueLike(eventData)) {
      return this.unwrapDatasetLink(eventData, preventLinksExtraction);
    }

    return {
      value: eventData?.[dataValueField as string] ?? null,
      converted: false,
      extracted: true,
    };
  }

  private unwrapArray(
    items: any[],
    dataValueField: string,
    preventLinksExtraction?: boolean,
  ): ValueResult {
    if (items.every((item) => isDatasetLinkValue(item))) {
      const value = preventLinksExtraction
        ? items.map((item) => ({ rowId: item.rowId, display: item.display }))
        : items.map((item) => item.rowId);
      return { value, converted: false, extracted: !preventLinksExtraction };
    }

    if (items.every((item) => typeof item === 'object')) {
      return {
        value: items.map((item) => item?.[dataValueField]),
        converted: false,
        extracted: true,
      };
    }

    return { value: items, converted: false, extracted: false };
  }

  private unwrapDatasetLink(eventData: any, preventLinksExtraction?: boolean): ValueResult {
    if (preventLinksExtraction) {
      return {
        value: { rowId: eventData.rowId, display: eventData.display },
        converted: false,
        extracted: false,
      };
    }
    return { value: eventData.rowId, converted: false, extracted: true };
  }

  private coerceByColumnType(params: GetChildValueParams, result: ValueResult): ValueResult {
    const { modelField, eventData, formDataBindings, inputElement, instanceValue, formContext } =
      params;

    const dsColumn = findColumn(
      formDataBindings.find((data) => data?.key === 'value')?.dataSource?.columns,
      modelField,
    );
    const dataType = dsColumn?.dataType;
    const { value, converted, extracted } = result;

    if (typeof value === 'string') {
      if (dataType === 'Boolean') {
        return { value: valueToBool(value), converted: true, extracted };
      }

      if (dataType === 'Tag' && dsColumn?.multipleValues) {
        return { value: [value], converted: true, extracted };
      }

      const labelValue = this.resolveLabelFromBindings(dataType, eventData, inputElement);
      if (labelValue !== undefined) {
        return isDatasetLinkValue(labelValue)
          ? { value: labelValue.display, converted: false, extracted: true }
          : { value: labelValue, converted: false, extracted };
      }
    }

    if (dataType === 'DateTime') {
      return {
        value: this.adjustDateForCalendarTimezone(value, instanceValue, formContext),
        converted,
        extracted,
      };
    }

    if (Array.isArray(eventData) && !eventData.length) {
      return { value: [], converted: true, extracted };
    }

    return { value, converted, extracted };
  }

  private resolveLabelFromBindings(
    dataType: string | undefined,
    eventData: any,
    inputElement: GetChildValueParams['inputElement'],
  ): any | undefined {
    if (
      isTimezoneDropdown(inputElement) ||
      dataType !== 'String' ||
      !eventData ||
      Array.isArray(eventData) ||
      typeof eventData !== 'object' ||
      !hasDataProps(inputElement)
    ) {
      return undefined;
    }

    const bindings = getActiveBinding(inputElement.props.data, 'optionsList')?.bindings;
    const labelField = bindings?.label
      ? this.schemaService.getColumnName(bindings.label)
      : undefined;

    return labelField ? eventData[labelField] : undefined;
  }

  private shouldTriggerFormulasRefresh({
    formInstance,
    instanceSchema,
  }: GetChildValueParams): boolean {
    if (formInstance?.elementType === 'formContainer') {
      return !!instanceSchema?.columns.some(isRefreshableFormula);
    }

    if (formInstance?.elementType === 'editableList') {
      return !!instanceSchema?.columns[0]?.dataTypeDef?.columns.some(isRefreshableFormula);
    }

    return false;
  }

  private adjustDateForCalendarTimezone(
    value: string,
    instanceValue: unknown,
    formContext?: FormContext,
  ): string {
    if (!formContext?.timeZoneColumn || !value) {
      return value;
    }

    const timeZone = (instanceValue as any)?.[formContext.timeZoneColumn];
    if (!timeZone) {
      return value;
    }

    return calendarWallTimeToUtc(new Date(value), timeZone).toISOString();
  }
}

// ─── standalone predicate (used as callback reference) ────────────────────────

function isRefreshableFormula(column: DatasetColumn): boolean {
  return column.columnType === 'Formula' && !!column.formula && !isLinkedInlineColumn(column);
}
